import { NavigationClient, NavigationOptions } from '@azure/msal-browser';
import { Component } from 'vue';
import { RouteMeta, RouteRecordRaw, Router } from 'vue-router';

type RouteOptions = {
  path?: string;
  meta?: RouteMeta;
  children?: RouteRecordRaw[];
  redirect?: RouteRecordRaw['redirect'];
  beforeEnter?: RouteRecordRaw['beforeEnter'];
};

function getRouteOptions(metaOrOptions?: RouteMeta | RouteOptions, path?: string): RouteOptions {
  if (!metaOrOptions) {
    return { path };
  }

  if (path !== undefined) {
    return { meta: metaOrOptions as RouteMeta, path };
  }

  const options = metaOrOptions as RouteOptions;
  if ('path' in options || 'children' in options || 'redirect' in options || 'beforeEnter' in options) {
    return options;
  }

  return { meta: metaOrOptions as RouteMeta };
}

/**
 * Helper function to create route meta
 */
export function meta(overrides: RouteMeta = {}): RouteMeta {
  return {
    authRequired: true,
    ...overrides,
  };
}

/**
 * Helper function to create a route
 */
export function route(
  name: string,
  component: Component | RouteRecordRaw['component'],
  metaOrOptions?: RouteMeta | RouteOptions,
  path?: string,
): RouteRecordRaw {
  const options = getRouteOptions(metaOrOptions, path);
  return {
    path: options.path || `/${name.toLowerCase()}`,
    name,
    component,
    ...(options.meta ? { meta: options.meta } : {}),
    ...(options.children ? { children: options.children } : {}),
    ...(options.redirect ? { redirect: options.redirect } : {}),
    ...(options.beforeEnter ? { beforeEnter: options.beforeEnter } : {}),
  } as RouteRecordRaw;

}

/**
 * Helper function to create a lazy route
 */
export function lazyRoute(
  name: string,
  loader: () => Promise<Component | { default: Component }>,
  metaOrOptions?: RouteMeta | RouteOptions,
  path?: string,
): RouteRecordRaw {
  return route(name, () => loader(), metaOrOptions, path);
}

/**
 * Helper function to create a redirect route
 */
export function redirect(path: string, targetName: string, name?: string, meta?: RouteMeta): RouteRecordRaw {
  return {
    path,
    redirect: { name: targetName },
    ...(name ? { name } : {}),
    ...(meta ? { meta } : {}),
  } as RouteRecordRaw;
}

/**
 * Override MSAL route navigation
 */
export class VueNavigationClient extends NavigationClient {
  private router: Router

  constructor (router: Router) {
    super()
    this.router = router
  }

  /**
   * Only called during redirects
   */
  navigateExternal (url: string, options: NavigationOptions): Promise<boolean> {
    return super.navigateExternal(url, options)
  }

  /**
   * Only called during popup completion
   */
  async navigateInternal (url: string, options: NavigationOptions): Promise<boolean> {
    const path = url.replace(location.origin, '')
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    options.noHistory
      ? await this.router.replace(path)
      : await this.router.push(path)
    return true
  }
}
